import type { RouteRecordRaw } from 'vue-router';

import { DEV_FEATURE, ORG_FEATURES, PERMISSION } from '~/constants';
import { useDevFeaturesStore } from '~/store';

import { useBookingTemplateConfig } from '~/features/useBookingTemplateConfig';
import { makeKeyBuilder, makeOrganisationRoute } from '~/features/useRouteMeta';

const OrganisationBookingsIndex = () =>
  import('~/pages/OrganisationBookingsIndex.vue');
const OrganisationBookingsOverview = () =>
  import('~/pages/OrganisationBookingsOverview.vue');
const OrganisationBookingsDeclaration = () =>
  import('~/pages/OrganisationBookingsDeclaration.vue');
const OrganisationBookingsNomination = () =>
  import('~/pages/OrganisationBookingsNomination.vue');
const OrganisationBookingsDeclarationNew = () =>
  import('~/pages/OrganisationBookingsDeclarationNew.vue');
const OrganisationBookingsHistory = () =>
  import('~/pages/OrganisationBookingsHistory.vue');
const OrganisationBookingsDeclarationVeson = () =>
  import('~/pages/OrganisationBookingsDeclarationVeson.vue');
const OrganisationBookingsNominationNew = () =>
  import('~/pages/OrganisationBookingsNominationNew.vue');

export const organisationBookingsRoutes: Readonly<RouteRecordRaw[]> = [
  makeOrganisationRoute({
    path: '/cargo-bookings',
    component: OrganisationBookingsIndex,
    keyBuilder: makeKeyBuilder('OrganisationBookingsIndex'),
    devFeature: DEV_FEATURE.CARGO_NOMINATION,
    orgFeature: ORG_FEATURES.BOOKINGS, // temporary check until bookings get released to all organisations
    orgPermission: () => {
      const { isExternalWithMainTemplate } = useBookingTemplateConfig();
      return !isExternalWithMainTemplate;
    },
    children: [
      {
        name: 'OrganisationBookingsIndex',
        path: '',
        redirect: () => {
          const devFeatures = useDevFeaturesStore();
          if (
            useBookingTemplateConfig().isInternalWithMainTemplate &&
            !devFeatures.CARGO_BOOKING_OVERVIEW
          ) {
            return { name: 'OrganisationBookingsDeclaration' };
          }
          return { name: 'OrganisationBookingsOverview' };
        },
      },
      {
        name: 'OrganisationBookingsOverview',
        path: 'overview',
        component: OrganisationBookingsOverview,
        props: true,
      },
      {
        name: 'OrganisationBookingsDeclaration',
        path: 'declaration',
        component: OrganisationBookingsDeclaration,
        props: true,
      },
      {
        name: 'OrganisationBookingsNomination',
        path: 'nomination',
        component: OrganisationBookingsNomination,
        props: true,
      },
    ],
  }),
  makeOrganisationRoute({
    name: 'OrganisationBookingsDeclarationNew',
    path: '/cargo-bookings/declaration/new',
    component: OrganisationBookingsDeclarationNew,
    devFeature: DEV_FEATURE.CARGO_NOMINATION,
    orgFeature: ORG_FEATURES.BOOKINGS,
    permission: PERMISSION.BOOKINGS_CREATE_OWN,
    orgPermission: () => {
      // Internal users should be able to navigate into /cargo-bookings/declaration/new if the booking template
      // is "main" even they don't have "PERMISSION.BOOKINGS_CREATE_OWN"
      // External users should be disallowed from navigate into /cargo-bookings/declaration/new if the booking template
      // is "main" even they have "PERMISSION.BOOKINGS_CREATE_OWN"
      // All other templates should be handle by "PERMISSION.BOOKINGS_CREATE_OWN"
      if (useBookingTemplateConfig().isExternalWithMainTemplate) return false;
      if (useBookingTemplateConfig().isInternalWithMainTemplate) return true;
      return undefined;
    },
  }),
  makeOrganisationRoute({
    name: 'OrganisationBookingsNominationNew',
    path: '/cargo-bookings/nomination/new',
    component: OrganisationBookingsNominationNew,
    devFeature: DEV_FEATURE.CARGO_NOMINATION_NOMINATE,
    orgFeature: ORG_FEATURES.BOOKINGS,
    permission: PERMISSION.BOOKINGS_UPDATE_ANY,
    orgPermission: () =>
      useBookingTemplateConfig().isInternalWithMainTemplate ? false : undefined,
  }),
  makeOrganisationRoute({
    name: 'OrganisationBookingsHistory',
    path: '/cargo-bookings/history',
    component: OrganisationBookingsHistory,
    devFeature: DEV_FEATURE.CARGO_NOMINATION,
    orgFeature: ORG_FEATURES.BOOKINGS,
    permission: PERMISSION.BOOKINGS_VIEW_OWN,
    orgPermission: () =>
      useBookingTemplateConfig().isExternalWithMainTemplate ? false : undefined,
  }),
  makeOrganisationRoute({
    name: 'OrganisationBookingsDeclarationVeson',
    path: '/cargo-bookings/declaration/:bookingId/transfer-to-veson',
    component: OrganisationBookingsDeclarationVeson,
    orgFeature: ORG_FEATURES.BOOKINGS,
    permission: PERMISSION.BOOKINGS_VIEW_OWN,
    orgPermission: () =>
      useBookingTemplateConfig().isExternalWithMainTemplate ? false : undefined,
    props: (route) => ({
      bookingId: route.params.bookingId as string,
    }),
  }),
];
